import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import ReactLoading from 'react-loading';
import DataTable from 'react-data-table-component';
import { toast } from 'react-toastify';
import api from 'api';

import { primary } from 'styles/colorProvider';
import { Header, SideMenu, Input, Button } from 'components';
import ExportToExcel from 'components/ExportToExcel/index';
import {
  Container,
  ContentArea,
  Title,
  Div,
  SearchRow,
  ClientCard,
  TableArea,
  GroupPanel,
  ActionsRow,
} from './styles';

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: Cookies.get('splitrisk-authtoken'),
});

const canUnify = () => {
  try {
    const permissions = JSON.parse(Cookies.get('splitrisk-backoffice-permissions') || '[]');
    return Boolean(permissions.find((p) => p.feature === 'unificar-boletos')?.hasPermission);
  } catch (error) {
    return false;
  }
};

function Billets() {
  const [document, setDocument] = useState('');
  const [loading, setLoading] = useState(false);
  const [client, setClient] = useState(null);
  const [selectedPolicies, setSelectedPolicies] = useState([]);
  const [group, setGroup] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [customDueDay, setCustomDueDay] = useState('');
  const [activeGroups, setActiveGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const allowUnify = canUnify();

  const loadGroup = async (id_group) => {
    const resp = await api({ method: 'GET', url: `/unified-billing/${id_group}`, headers: authHeaders() });
    setGroup(resp?.data);
  };

  const loadActiveGroups = async () => {
    setLoadingGroups(true);
    try {
      const resp = await api({ method: 'GET', url: `/unified-billing/groups`, headers: authHeaders() });
      setActiveGroups(resp?.data || []);
    } catch (error) {
      toast.error('Não foi possível carregar os grupos unificados');
    }
    setLoadingGroups(false);
  };

  useEffect(() => {
    loadActiveGroups();
  }, []);

  const refreshClient = async (doc = document) => {
    if (!doc) {
      setClient(null);
      return null;
    }
    const resp = await api({
      method: 'GET',
      url: `/unified-billing/search`,
      params: { document: doc },
      headers: authHeaders(),
    });
    setClient(resp?.data);
    return resp?.data;
  };

  const openGroupFromGrid = async (row) => {
    setDocument(row.TB_USERS?.cpf || '');
    setClient({ id: row.TB_USERS?.id, name: row.TB_USERS?.name, document: row.TB_USERS?.cpf });
    await loadGroup(row.id);
  };

  const handleSearch = async () => {
    if (!document) return;
    setLoading(true);
    setGroup(null);
    try {
      const data = await refreshClient(document);
      const groupIds = [...new Set((data?.policies || []).map((p) => p.unified_group_id).filter(Boolean))];
      if (groupIds.length === 1) {
        await loadGroup(groupIds[0]);
      } else if (groupIds.length > 1) {
        toast.info(`Cliente possui ${groupIds.length} grupos ativos. Abra o desejado na lista de grupos.`);
      }
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Cliente não encontrado');
      setClient(null);
    }
    setLoading(false);
  };

  const handleSelection = ({ selectedRows }) => setSelectedPolicies(selectedRows);

  const handleCreateGroup = async () => {
    if (!allowUnify) {
      toast.error('Você não tem permissão para unificar boletos');
      return;
    }
    if (selectedPolicies.length < 2) {
      toast.error('Selecione ao menos 2 apólices para unificar');
      return;
    }
    if (customDueDay) {
      const day = Number(customDueDay);
      if (!Number.isInteger(day) || day < 1 || day > 28) {
        toast.error('Dia de vencimento deve ser entre 1 e 28');
        return;
      }
    }
    if (!window.confirm(`Unificar ${selectedPolicies.length} apólices e gerar o boleto agora?`)) return;

    setLoading(true);
    try {
      const resp = await api({
        method: 'POST',
        url: `/unified-billing`,
        headers: authHeaders(),
        data: {
          id_user: client.id,
          id_policies: selectedPolicies.map((p) => p.id),
          due_day: customDueDay || undefined,
        },
      });
      toast.success('Grupo criado e boleto unificado gerado');
      await loadGroup(resp.data.id);
      setSelectedPolicies([]);
      setCustomDueDay('');
      loadActiveGroups();
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Não foi possível criar o grupo');
    }
    setLoading(false);
  };

  const handleGenerateCharge = async () => {
    if (!allowUnify) {
      toast.error('Você não tem permissão para unificar boletos');
      return;
    }
    if (!window.confirm('Gerar um novo boleto unificado agora?')) return;

    setGenerating(true);
    try {
      await api({
        method: 'POST',
        url: `/unified-billing/${group.id}/generate-charge`,
        headers: authHeaders(),
      });
      toast.success('Boleto unificado gerado');
      await loadGroup(group.id);
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Não foi possível gerar o boleto');
    }
    setGenerating(false);
  };

  const handleRemovePolicy = async (id_policy) => {
    if (!allowUnify) {
      toast.error('Você não tem permissão para unificar boletos');
      return;
    }
    if (!window.confirm('Remover esta apólice do grupo e voltar à cobrança individual?')) return;

    try {
      const resp = await api({
        method: 'DELETE',
        url: `/unified-billing/${group.id}/policies/${id_policy}`,
        headers: authHeaders(),
      });
      if (resp?.data?.groupCanceled) {
        toast.success('Grupo desfeito. Cobrança individual restaurada.');
        setGroup(null);
        await refreshClient();
        loadActiveGroups();
      } else {
        toast.success('Placa removida do grupo. Cobrança individual restaurada.');
        await loadGroup(group.id);
        await refreshClient();
      }
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Não foi possível remover');
    }
  };

  const handleAddPolicy = async (id_policy) => {
    if (!allowUnify) {
      toast.error('Você não tem permissão para unificar boletos');
      return;
    }
    if (!window.confirm('Incluir esta apólice no grupo unificado?')) return;

    try {
      await api({
        method: 'POST',
        url: `/unified-billing/${group.id}/policies`,
        headers: authHeaders(),
        data: { id_policy },
      });
      toast.success('Placa incluída no grupo');
      await loadGroup(group.id);
      await refreshClient();
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Não foi possível incluir');
    }
  };

  const handleCancelGroup = async () => {
    if (!allowUnify) {
      toast.error('Você não tem permissão para unificar boletos');
      return;
    }
    if (!window.confirm('Desfazer a unificação e restaurar cobranças individuais?')) return;

    try {
      const resp = await api({ method: 'POST', url: `/unified-billing/${group.id}/cancel`, headers: authHeaders() });
      toast.success('Unificação desfeita. Cobranças individuais restauradas.');
      if (resp?.data?.restoreErrors?.length) {
        toast.warn('Algumas assinaturas individuais falharam ao restaurar. Verifique o log.');
      }
      setGroup(null);
      await refreshClient();
      loadActiveGroups();
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Não foi possível desfazer');
    }
  };

  const selectableColumns = [
    { name: 'Apólice', selector: (row) => row.policy_number || row.id, sortable: true },
    { name: 'Placa', selector: (row) => row.plate || '-' },
    { name: 'Valor', selector: (row) => `R$ ${Number(row.value || 0).toFixed(2)}` },
    { name: 'Vencimento', selector: (row) => (row.due_day ? `dia ${row.due_day}` : '-') },
    {
      name: 'Situação',
      selector: (row) => (row.unified_group_id ? 'Já unificada' : row.is_fleet ? 'Frota' : 'Elegível'),
    },
  ];

  const groupMembers = group?.TB_UNIFIED_BILLING_GROUP_POLICIES?.filter((m) => m.status === 'active') || [];
  const availableToAdd =
    client?.policies?.filter(
      (p) => !p.unified_group_id && !p.is_fleet && !groupMembers.find((m) => m.id_policy === p.id),
    ) || [];
  const lastCharge = group?.TB_UNIFIED_BILLING_CHARGES?.[0];

  const activeGroupsExportData = activeGroups.map((row) => ({
    Cliente: row.TB_USERS?.name,
    'CPF/CNPJ': row.TB_USERS?.cpf,
    Telefone: row.TB_USERS?.phone,
    Placas: row.TB_UNIFIED_BILLING_GROUP_POLICIES?.length || 0,
    'Dia Vencimento': row.due_day,
    'Valor Última Cobrança': row.TB_UNIFIED_BILLING_CHARGES?.[0]?.value_total?.toFixed(2) || '',
    'Status Última Cobrança': row.TB_UNIFIED_BILLING_CHARGES?.[0]?.status || 'Nenhuma gerada',
    'Link do Boleto': row.TB_UNIFIED_BILLING_CHARGES?.[0]?.invoice_url || '',
  }));

  return (
    <>
      <Header />
      <Container>
        <SideMenu />
        <ContentArea>
          <Div>
            <Title>UNIFICAÇÃO DE BOLETOS</Title>
            {(client || group) && (
              <Button
                width="140px"
                height="32px"
                onClick={() => {
                  setClient(null);
                  setGroup(null);
                  setDocument('');
                  loadActiveGroups();
                }}
              >
                Voltar
              </Button>
            )}
          </Div>

          <SearchRow>
            <Input
              width="300px"
              placeholder="CPF ou CNPJ do segurado"
              value={document}
              onChange={(e) => setDocument(e.target.value)}
            />
            <Button width="120px" onClick={handleSearch} disabled={loading}>
              {loading ? <ReactLoading color={primary} height={18} width={18} type="spin" /> : 'Buscar'}
            </Button>
          </SearchRow>

          {!client && !group && (
            <TableArea>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <p>Grupos de unificação ativos</p>
                {activeGroups.length > 0 && (
                  <div style={{ width: '160px' }}>
                    <ExportToExcel
                      exportData={activeGroupsExportData}
                      fileName={`unificacao_boletos_${new Date().toLocaleDateString('pt-BR').replace(/\//g, '-')}`}
                    />
                  </div>
                )}
              </div>
              <DataTable
                persistTableHead
                responsive
                progressPending={loadingGroups}
                columns={[
                  { name: 'Cliente', selector: (row) => row.TB_USERS?.name },
                  { name: 'CPF/CNPJ', selector: (row) => row.TB_USERS?.cpf },
                  { name: 'Placas', selector: (row) => row.TB_UNIFIED_BILLING_GROUP_POLICIES?.length || 0, width: '8%' },
                  { name: 'Dia venc.', selector: (row) => row.due_day, width: '8%' },
                  {
                    name: 'Última cobrança',
                    selector: (row) =>
                      row.TB_UNIFIED_BILLING_CHARGES?.[0]
                        ? `R$ ${Number(row.TB_UNIFIED_BILLING_CHARGES[0].value_total).toFixed(2)} · ${row.TB_UNIFIED_BILLING_CHARGES[0].status}`
                        : 'Nenhuma gerada ainda',
                  },
                  {
                    name: 'Boleto',
                    cell: (row) =>
                      row.TB_UNIFIED_BILLING_CHARGES?.[0]?.invoice_url ? (
                        <a
                          href={row.TB_UNIFIED_BILLING_CHARGES[0].invoice_url}
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: primary, fontWeight: 'bold' }}
                        >
                          Ver boleto
                        </a>
                      ) : (
                        '-'
                      ),
                  },
                  {
                    name: 'Ação',
                    cell: (row) => (
                      <Button width="100px" height="30px" onClick={() => openGroupFromGrid(row)}>
                        Abrir
                      </Button>
                    ),
                  },
                ]}
                data={activeGroups}
                noDataComponent={<small>Nenhum grupo de unificação ativo no momento</small>}
              />
            </TableArea>
          )}

          {client && (
            <ClientCard>
              <h3>{client.name}</h3>
              <span>{client.document}</span>
              {!client.id_asaas && (
                <span style={{ color: 'red' }}>Cliente sem customer Asaas cadastrado — não é possível unificar.</span>
              )}
            </ClientCard>
          )}

          {client && !group && (
            <TableArea>
              <p>Apólices do cliente</p>
              <DataTable
                persistTableHead
                responsive
                columns={selectableColumns}
                data={client.policies}
                selectableRows
                selectableRowDisabled={(row) => !!row.unified_group_id || !!row.is_fleet || !client.id_asaas}
                onSelectedRowsChange={handleSelection}
                noDataComponent={<small>Nenhuma apólice ativa encontrada</small>}
              />
              {allowUnify ? (
                <>
                  <ActionsRow>
                    <Input
                      width="220px"
                      height="35px"
                      type="number"
                      min="1"
                      max="28"
                      placeholder="Dia de vencimento (1-28)"
                      value={customDueDay}
                      onChange={(e) => setCustomDueDay(e.target.value)}
                    />
                    <Button
                      width="220px"
                      onClick={handleCreateGroup}
                      disabled={loading || selectedPolicies.length < 2 || !client.id_asaas}
                    >
                      Unificar selecionadas ({selectedPolicies.length})
                    </Button>
                  </ActionsRow>
                  <small style={{ color: '#888' }}>
                    Se não informado, usa o menor dia de vencimento entre as apólices selecionadas.
                  </small>
                </>
              ) : (
                <small style={{ color: '#888' }}>Você não tem permissão para unificar boletos.</small>
              )}
            </TableArea>
          )}

          {group && (
            <GroupPanel>
              <h3>Grupo de unificação #{group.id}</h3>
              <span>Dia de vencimento: {group.due_day}</span>

              <TableArea>
                <p>Apólices no grupo</p>
                <DataTable
                  persistTableHead
                  responsive
                  columns={[
                    { name: 'Apólice', selector: (row) => row.TB_POLICIES?.policy_number || row.id_policy },
                    { name: 'Placa', selector: (row) => row.TB_POLICIES?.TB_VEHICLE?.plate || '-' },
                    {
                      name: 'Ação',
                      cell: (row) =>
                        allowUnify ? (
                          <Button width="100px" height="30px" onClick={() => handleRemovePolicy(row.id_policy)}>
                            Remover
                          </Button>
                        ) : (
                          '-'
                        ),
                    },
                  ]}
                  data={groupMembers}
                  noDataComponent={<small>Nenhuma apólice ativa no grupo</small>}
                />
              </TableArea>

              {allowUnify && availableToAdd.length > 0 && (
                <TableArea>
                  <p>Outras apólices do cliente (incluir no grupo)</p>
                  <DataTable
                    persistTableHead
                    responsive
                    columns={[
                      { name: 'Apólice', selector: (row) => row.policy_number || row.id },
                      { name: 'Placa', selector: (row) => row.plate || '-' },
                      {
                        name: 'Ação',
                        cell: (row) => (
                          <Button width="100px" height="30px" onClick={() => handleAddPolicy(row.id)}>
                            Incluir
                          </Button>
                        ),
                      },
                    ]}
                    data={availableToAdd}
                  />
                </TableArea>
              )}

              {lastCharge && (
                <ClientCard>
                  <h3>Última cobrança gerada</h3>
                  <span>
                    Valor: R$ {Number(lastCharge.value_total).toFixed(2)} • Status: {lastCharge.status} • Venc.:{' '}
                    {new Date(lastCharge.due_date).toLocaleDateString('pt-BR')}
                  </span>
                  {lastCharge.invoice_url && (
                    <a href={lastCharge.invoice_url} target="_blank" rel="noreferrer">
                      Ver boleto
                    </a>
                  )}
                </ClientCard>
              )}

              {allowUnify && (
                <ActionsRow>
                  <Button width="220px" onClick={handleGenerateCharge} disabled={generating}>
                    {generating ? 'Gerando...' : 'Gerar boleto unificado agora'}
                  </Button>
                  <Button width="180px" onClick={handleCancelGroup}>
                    Desfazer unificação
                  </Button>
                </ActionsRow>
              )}
            </GroupPanel>
          )}
        </ContentArea>
      </Container>
    </>
  );
}

export default Billets;
